import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { VehicleSEO } from './SEOComponents';
import { getAttribution, track, trackFormStart, trackLead } from '../lib/analytics';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { isCallForPrice } from '../utils/priceDisplay';
import {
  ChevronLeft, 
  ChevronRight, 
  Phone, 
  Mail, 
  Gauge, 
  Settings, 
  CircleDot,
  Calendar,
  Palette,
  Fuel,
  Car,
  DoorOpen,
  Users,
  Hash,
  CheckCircle,
  ArrowLeft,
  Share2,
  Loader,
  X,
  ZoomIn,
  Check,
  Tag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightbox Component
const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev, getImageSrc, onImageError }) => {
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X size={28} />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 bg-white/10 px-4 py-2 rounded-full text-white text-sm">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main image container */}
      <div className="relative w-full h-full flex items-center justify-center p-4 pb-32 md:p-16">
        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={onPrev}
            className="absolute left-4 md:left-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {/* Image */}
        <img
          src={getImageSrc ? getImageSrc(currentIndex, images) : images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={onNext}
            className="absolute right-4 md:right-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2 bg-black/50 rounded-lg">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => {
                // This will be handled by parent component
                const diff = index - currentIndex;
                if (diff > 0) {
                  for (let i = 0; i < diff; i++) onNext();
                } else if (diff < 0) {
                  for (let i = 0; i < Math.abs(diff); i++) onPrev();
                }
              }}
              className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-red-600' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [shareSuccess, setShareSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [trackedVehicleId, setTrackedVehicleId] = useState('');
  
  // Fallback image
  const fallbackImage = '/photos-coming-soon.jpg';
  
  const getImageSrc = (index, images) => {
    if (imageErrors[index] || !images[index]) {
      return fallbackImage;
    }
    return resolveImageUrl(images[index]) || fallbackImage;
  };
  
  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
    // Auto-advance if current image fails
    const imgs = vehicle?.images || [];
    if (index === currentImageIndex && index < imgs.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  };

  useEffect(() => {
    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!vehicle?.id || trackedVehicleId === vehicle.id) {
      return;
    }

    track('vehicle_view', vehicle);
    setTrackedVehicleId(vehicle.id);
  }, [vehicle, trackedVehicleId]);

  // Handle Share
  const handleShare = async () => {
    if (vehicle?.id) track('share', { id: vehicle.id });
    const shareData = {
      title: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Vehicle',
      text: vehicle ? `Check out this ${vehicle.year} ${vehicle.make} ${vehicle.model}${isCallForPrice(vehicle) ? '' : ` for $${Number(vehicle.price).toLocaleString()}`} at Big Chief Motors!` : '',
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      }
    } catch (err) {
      // User cancelled or error - try clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch (e) {
        console.error('Share failed:', e);
      }
    }
  };

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(response.data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <Loader size={48} className="animate-spin mx-auto text-red-600" />
          <p className="text-gray-600 mt-4">Loading vehicle details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-black mb-4">Vehicle Not Found</h1>
          <p className="text-gray-600 mb-8">The vehicle you're looking for is no longer available.</p>
          <Button onClick={() => navigate('/inventory')} className="bg-red-600 hover:bg-red-700 text-white">
            Browse Inventory
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const images = vehicle.images || [];
  
  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = 'auto';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        vehicle_id: vehicle.id,
        vehicle_title: vehicle.title,
        attribution: getAttribution(),
      });
      trackLead('vehicle_inquiry', { vehicle_id: vehicle.id, vehicle_title: vehicle.title, value: Number(vehicle.price) || 0 });
      alert('Thank you for your inquiry! We will contact you shortly.');
      setShowContactForm(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      // Never tell the customer it worked when it didn't — keep the form + data so they can retry.
      alert("Sorry — we couldn't submit your inquiry just now. Please try again, or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const isSold = vehicle.status === 'Sold';
  const isMarineOrPowersports = ['Boat', 'Personal Watercraft', 'Snowmobile', 'ATV', 'UTV', 'Motorcycle', 'Trailer'].includes(vehicle.bodyType);

  const specs = [
    { icon: Calendar, label: 'Year', value: vehicle.year },
    { icon: Tag, label: 'Trim', value: vehicle.trim },
    { icon: Gauge, label: 'Kilometers', value: `${(vehicle.km || 0).toLocaleString()} km` },
    ...(!isMarineOrPowersports ? [
      { icon: Settings, label: 'Transmission', value: vehicle.transmission },
      { icon: CircleDot, label: 'Drivetrain', value: vehicle.drivetrain },
    ] : []),
    { icon: Car, label: 'Type', value: vehicle.bodyType },
    { icon: Palette, label: 'Exterior', value: vehicle.exteriorColor },
    { icon: Palette, label: 'Interior', value: vehicle.interiorColor },
    { icon: Fuel, label: 'Fuel Type', value: vehicle.fuelType },
    ...(!isMarineOrPowersports ? [
      { icon: DoorOpen, label: 'Doors', value: vehicle.doors },
      { icon: Users, label: 'Seats', value: vehicle.seats },
    ] : []),
    { icon: Hash, label: 'Stock #', value: vehicle.stockNumber },
  ].filter(spec => spec.value);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* SEO Meta Tags */}
      <VehicleSEO vehicle={vehicle} baseUrl={BACKEND_URL?.replace('/api', '') || window.location.origin} />
      
      <Header />
      
      {/* Lightbox */}
      {showLightbox && images.length > 0 && (
        <ImageLightbox
          images={images}
          currentIndex={currentImageIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
          getImageSrc={getImageSrc}
          onImageError={handleImageError}
        />
      )}
      
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-6 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-red-600">Home</Link>
          <span>/</span>
          <Link to="/inventory" className="hover:text-red-600">Inventory</Link>
          <span>/</span>
          <span className="text-black truncate">{vehicle.year} {vehicle.make} {vehicle.model}</span>
        </div>

        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/inventory')} 
          className="mb-4 sm:mb-6 text-gray-600 hover:text-red-600 p-0 text-sm"
        >
          <ArrowLeft size={18} className="mr-1.5" />
          Back to Inventory
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Image Gallery */}
          <div>
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg">
              {/* Main Image */}
              <div 
                className="relative aspect-[4/3] cursor-pointer group bg-gray-100"
                onClick={() => openLightbox(currentImageIndex)}
              >
                <img
                  src={getImageSrc(currentImageIndex, images)}
                  alt={vehicle.title}
                  className="w-full h-full object-contain"
                  onError={() => handleImageError(currentImageIndex)}
                />
                {/* Zoom overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                    <ZoomIn size={24} className="text-gray-800" />
                  </div>
                </div>
                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                  {vehicle.condition && (
                    <Badge className={`${vehicle.condition === 'New' ? 'bg-green-600' : 'bg-blue-600'} text-white text-sm font-medium px-3 py-1`}>
                      {vehicle.condition}
                    </Badge>
                  )}
                  {vehicle.badges && vehicle.badges.map((badge, index) => (
                    <Badge
                      key={index}
                      className={`${
                        badge === 'Low km' ? 'bg-green-500' : 'bg-red-600'
                      } text-white text-sm font-medium px-3 py-1`}
                    >
                      {badge}
                    </Badge>
                  ))}
                </div>
                {/* Navigation arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
                {/* Image counter */}
                {images.length > 0 && (
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
                {/* Click to expand hint */}
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <ZoomIn size={14} />
                  Click to expand
                </div>
              </div>
            </div>

            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-24 aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex ? 'border-red-600' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img 
                      src={getImageSrc(index, images)} 
                      alt="" 
                      className="w-full h-full object-cover" 
                      onError={() => handleImageError(index)}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 sm:gap-4 mt-4 sm:mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 border-gray-300 text-sm ${shareSuccess ? 'bg-green-50 border-green-500 text-green-600' : ''}`}
                onClick={handleShare}
              >
                {shareSuccess ? (
                  <>
                    <Check size={18} className="mr-2" />
                    Link Copied!
                  </>
                ) : (
                  <>
                    <Share2 size={18} className="mr-2" />
                    Share
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Vehicle Info */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-black mb-1">
              {vehicle.title}{vehicle.trim && !vehicle.title?.includes(vehicle.trim) ? ` ${vehicle.trim}` : ''}
            </h1>
            
            {isSold ? (
              <div className="mb-4 sm:mb-6">
                <span className="inline-block bg-red-600 text-white px-4 py-2 rounded-lg text-lg sm:text-xl font-bold uppercase tracking-wide">SOLD</span>
              </div>
            ) : (
              <p className="text-3xl sm:text-4xl font-bold text-red-600 mb-4 sm:mb-6">
                {isCallForPrice(vehicle) ? 'Call for price' : `$${Number(vehicle.price).toLocaleString()}`}
                {!isCallForPrice(vehicle) && (
                  <span className="text-sm sm:text-base font-normal text-gray-500 ml-2">Cash price excl. taxes</span>
                )}
              </p>
            )}

            {/* Quick specs */}
            <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6 text-sm sm:text-base">
              <div className="flex items-center gap-1.5 text-gray-600">
                <Gauge size={18} />
                <span>{(vehicle.km || 0).toLocaleString()} km</span>
              </div>
              {!isMarineOrPowersports && vehicle.transmission && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Settings size={18} />
                  <span>{vehicle.transmission}</span>
                </div>
              )}
              {!isMarineOrPowersports && vehicle.drivetrain && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <CircleDot size={18} />
                  <span>{vehicle.drivetrain}</span>
                </div>
              )}
              {vehicle.engine && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Fuel size={18} />
                  <span>{vehicle.engine}</span>
                </div>
              )}
            </div>

            {/* Contact CTA */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Interested in this vehicle?</h3>
              <div className="space-y-2 sm:space-y-3">
                <a
                  href={`tel:${settings.phone}`}
                  className="flex items-center gap-3 p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Phone size={20} />
                  <span className="font-medium">Call Us: {settings.phone}</span>
                </a>
                <button
                  onClick={() => setShowContactForm(!showContactForm)}
                  className="flex items-center gap-3 p-3 w-full border-2 border-black text-black rounded-lg hover:bg-black hover:text-white transition-colors"
                >
                  <Mail size={20} />
                  <span className="font-medium">Send Inquiry</span>
                </button>
                <Link
                  to={`/book-test-drive?vehicle=${encodeURIComponent([vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '))}&vehicleId=${encodeURIComponent(vehicle.id)}`}
                  className="flex items-center gap-3 p-3 w-full border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
                >
                  <Calendar size={20} />
                  <span className="font-medium">Book a Test Drive</span>
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            {showContactForm && (
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h3 className="text-lg font-semibold mb-4">Send Us a Message</h3>
                <form onSubmit={handleSubmit} onFocus={() => trackFormStart('vehicle_inquiry')} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <textarea
                    placeholder={`I'm interested in this ${vehicle.year} ${vehicle.make} ${vehicle.model}...`}
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            )}

            {/* Description */}
            {vehicle.description && (
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Description</h3>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{vehicle.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Specs Grid */}
        {specs.length > 0 && (
          <div className="mt-4 sm:mt-8 bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Vehicle Specifications</h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {specs.map((spec, index) => (
                <div key={index} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                  <spec.icon size={18} className="text-red-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500">{spec.label}</p>
                    <p className="font-medium text-black text-sm sm:text-base truncate">{spec.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        {vehicle.features && vehicle.features.length > 0 && (
          <div className="mt-4 sm:mt-8 bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Features & Equipment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {vehicle.features.flatMap((feature, index) => {
                if (feature.includes(',')) {
                  return feature.split(',').map((f, i) => (
                    <div key={`${index}-${i}`} className="flex items-center gap-2 text-gray-700 text-sm">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span>{f.trim()}</span>
                    </div>
                  ));
                }
                return (
                  <div key={index} className="flex items-center gap-2 text-gray-700 text-sm">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default VehicleDetail;
